import React from 'react';
import classNames from 'classnames';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import messageModule from './Message.module.css';
import { ENetworkRequestStatus } from 'services/BackendApi';
import { CopyIcon, EllipseComponent, thumbDownIcon, thumbUpIcon } from 'ui/Icons';
import { SvgIcon } from 'ui/SvgIcon';
import { theme } from '../../../../../tailwind.config';
import { enqueueNotification } from 'store/modules/ui/actions';
import { useDispatch } from 'react-redux';

export enum ESource {
  USER = 'user',
  CHATBOT = 'chatBot',
}

export interface IMessage {
  text?: string | null;
  date: Date;
  source: ESource;
  status: Exclude<ENetworkRequestStatus, ENetworkRequestStatus.IDLE>;
  rating: boolean | null;
  messageId: string;
  handleRateMessage: (messageId: string, rating: boolean) => void;
}

interface IMessageProps extends IMessage { }

function formatTime(date): string {
  let hours = date.getHours();
  let minutes = date.getMinutes();

  // Add leading zero to hours and minutes if needed
  if (hours < 10) {
    hours = '0' + hours;
  }
  if (minutes < 10) {
    minutes = '0' + minutes;
  }

  return hours + ':' + minutes;
}

// eslint-disable-next-line unicorn/no-new-array
const Ellipses = new Array(3).fill(<EllipseComponent />);

export const Message: React.FC<IMessageProps> = ({ source, text, date, status, rating, messageId, handleRateMessage }) => {
  const dispatch = useDispatch();
  const isLoading = status === ENetworkRequestStatus.PENDING && source === ESource.CHATBOT;
  const renderer = new marked.Renderer();
  const linkRenderer = renderer.link;
  renderer.link = ({ href, title, text: linkText, tokens }) => {
    const html = linkRenderer.call(renderer, { href, title, text: linkText, tokens });
    return html.replace(/^<a /, '<a target="_blank" rel="noreferrer noopener nofollow"');
  };
  const messageContent = source === ESource.CHATBOT && text ? marked.parse(text, { renderer, async: false }).trim() : text;

  if (status === ENetworkRequestStatus.ERROR)
    return null;
  return (
    <div className={classNames('flex flex-col w-[calc(100%-40px)] gap-5px', {
      'place-self-end': source === ESource.USER,
      'place-self-start': source === ESource.CHATBOT,
    })}>
      <div className={classNames('w-full p-2.5 whitespace-pre-line rounded-tl-lg rounded-tr-lg justify-start items-center gap-2.5 inline-flex', {
        'self-end rounded-bl-lg bg-gray-100': source === ESource.USER,
        'self-start rounded-br-lg bg-brown-prime-10': source === ESource.CHATBOT,
        '!w-fit': status === ENetworkRequestStatus.PENDING && source === ESource.CHATBOT,
      })}>
        {
          isLoading ? (
            <div className={classNames('message text-[15px] font-normal font-pt-sans leading-snug', messageModule.message, {
              'text-black': source === ESource.CHATBOT,
            })}
            >
              <div className="flex gap-[5px]">{Ellipses}</div>
            </div>
          ) : (
            <div className={classNames('message text-[15px] font-normal font-pt-sans leading-snug', messageModule.message, {
              'text-white': source === ESource.USER,
            })}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(messageContent ?? '', { ADD_ATTR: ['target'] }) }}
            />
          )
        }
      </div>
      {
        !(source === ESource.CHATBOT && status !== ENetworkRequestStatus.SUCCESS) && (
          <>
            {source === ESource.CHATBOT && status === ENetworkRequestStatus.SUCCESS && (
              <div className='flex gap-15px'>
                <SvgIcon
                  IconComponent={CopyIcon}
                  width="18px"
                  height="18px"
                  className='cursor-pointer'
                  defaultFill={theme.colors['brown-prime']}
                  onClick={() => {
                    navigator.clipboard.writeText(text || '').then(() => {
                      dispatch(
                        enqueueNotification({
                          message: `Copied to clipboard`,
                          options: { variant: 'success' },
                        })
                      )
                    });
                  }}
                />
                <SvgIcon
                  IconComponent={thumbUpIcon}
                  className='w-[17px] h-[15px] cursor-pointer'
                  defaultFill={theme.colors[rating ? 'brown-prime' : 'white']}
                  stroke={theme.colors['brown-prime']}
                  onClick={() => rating !== true && handleRateMessage(messageId, true)}
                />
                <SvgIcon
                  IconComponent={thumbDownIcon}
                  className='w-[17px] h-[15px] cursor-pointer'
                  defaultFill={theme.colors[rating === false ? 'brown-prime' : 'white']}
                  stroke={theme.colors['brown-prime']}
                  onClick={() => rating !== false && handleRateMessage(messageId, false)}
                />
              </div>
            )}
            <div className={classNames('text-sm text-gray-100', {
              'self-end': source === ESource.USER,
            })}>
              {formatTime(date)}
            </div>
          </>
        )
      }
    </div>
  );
};